import { BitkitTabs, IconWarningYellow } from '@bitrise/bitkit-v2';
import { DateTime } from 'luxon';
import { useContext, useEffect, useMemo } from 'react';

import BuildArtifactList from '@/components/BuildArtifacts/ArtifactList/BuildArtifactList';
import useHtmlReports from '@/components/BuildArtifacts/ArtifactList/hooks/useHtmlReports';
import BuildCacheOfBuild from '@/components/BuildCache/BuildCacheOfBuild';
import useBuildCacheOfBuildTab from '@/components/BuildCache/hooks/useBuildCacheOfBuildTab';
import { GlobalPropsContext } from '@/components/page/GlobalPropsContext';
import { calculateSummary } from '@/components/test-results/TestResultsExplorer/TestResultsExplorer.utils';
import { BuildDetailsTabId } from '@/enums/BuildDetailsTabs';
import useArtifacts from '@/hooks/api/useArtifacts';
import useTabsQueryParam from '@/hooks/useTabsQueryParam';
import { isBuildFinished, isBuildNotFinished } from '@/models/Build';
import { LogFormat } from '@/models/LogFormat';
import useBuildDetailsPageStore, { updateCacheInfo } from '@/pages/BuildDetailsPage/BuildDetailsPage.store';
import useBuildCacheCacheInfo from '@/pages/BuildDetailsPage/components/BuildDetailsTabs/hooks/useBuildCacheCacheInfo';
import ResourceUsageMonitoring from '@/pages/BuildDetailsPage/components/BuildDetailsTabs/ResourceUsageMonitoring';
import { useTrackBuildDetailsTabChange } from '@/pages/BuildDetailsPage/hooks/useBuildDetailsAnalytics';
import { getStandardErrorMessage } from '@/utils/errorResponseParser';
import { buildDetailsPagePath, planSelectorPagePath, workflowEditorPath } from '@/utils/monolithApiRouteService';

import useBuildLogSummary from '../../hooks/useBuildLogSummary';
import AIAssistantCard from '../AIAssistant/AIAssistant';
import BuildTestResults from '../BuildTestResults/BuildTestResults';
import BuildLogContainer from './BuildLogContainer';
import Details from './Details';
import useBuildTestResults from './hooks/useBuildTestResults';
import {
  getArtifactsBadge,
  getDefaultTab,
  getTestsTabBadge,
  showDeployToBitriseIoWarning,
} from './utils/BuildDetailsTabs.utils';

type BuildDetailsTabsProps = {
  canSpeedUpBuild?: boolean;
};

const BuildDetailsTabs = ({ canSpeedUpBuild = false }: BuildDetailsTabsProps) => {
  const { account, user } = useContext(GlobalPropsContext);

  const { app, build, initBuild, abilities, cacheInfo, stack, isViewedAsPublicPage } = useBuildDetailsPageStore();
  const { canViewBuildLogs, canViewBuildArtifacts, canViewBuildCache, canChangeSettings } = abilities;

  const {
    data: summary,
    isLoading: isSummaryLoading,
    showStructuredBuildLog,
  } = useBuildLogSummary({
    appSlug: app.slug,
    buildSlug: build.slug,
    enabled: build.logFormat === LogFormat.JSON && isBuildFinished(build),
    isViewedAsPublicPage,
  });

  const selfHostedAgent = stack?.id.startsWith('agent-pool-');

  const artifactsEnabled = canViewBuildArtifacts && isBuildFinished(build);

  const { artifacts } = useArtifacts({
    appSlug: app.slug,
    buildSlug: build.slug,
    enabled: artifactsEnabled,
  });

  const { htmlReports } = useHtmlReports({
    appSlug: app.slug,
    buildSlug: build.slug,
    options: { enabled: artifactsEnabled },
  });

  const { data: buildCacheInfo } = useBuildCacheCacheInfo({
    slug: build.slug,
    initialData: cacheInfo,
    enabled: Boolean(abilities.canViewBuildCache && isBuildFinished(build)),
  });

  useEffect(() => {
    if (buildCacheInfo) {
      updateCacheInfo(buildCacheInfo);
    }
  }, [buildCacheInfo]);

  const {
    data: buildTestResults,
    isFetched: isTestResultsFetched,
    error: testResultsRequestError,
  } = useBuildTestResults({
    slug: build.slug,
    enabled: Boolean(account?.testReportingAvailable && isBuildFinished(build)),
  });

  const testSummary = useMemo(
    () => calculateSummary({ testSuites: buildTestResults?.testSuites || [] }),
    [buildTestResults],
  );
  const trackTabChange = useTrackBuildDetailsTabChange(
    build.slug,
    build.status,
    canSpeedUpBuild,
    app.projectId ?? 'unsetProjectId',
  );
  const { submit: trackBuildCacheOfBuildTab } = useBuildCacheOfBuildTab(app.owner?.slug ?? '', build.slug);

  const TAB_IDS = useMemo(() => {
    const ids: BuildDetailsTabId[] = [];
    if (canViewBuildLogs) {
      ids.push(BuildDetailsTabId.log);
    }
    ids.push(BuildDetailsTabId.tests);
    if (canViewBuildArtifacts) {
      ids.push(BuildDetailsTabId.artifacts);
    }
    if (canViewBuildCache && user) {
      ids.push(BuildDetailsTabId.buildCache);
    }
    if (!selfHostedAgent) {
      ids.push(BuildDetailsTabId.resources);
    }
    ids.push(BuildDetailsTabId.details);
    return ids;
  }, [canViewBuildCache, canViewBuildArtifacts, canViewBuildLogs, selfHostedAgent, user]);

  const { selectTab, tabId } = useTabsQueryParam<BuildDetailsTabId>({
    defaultId: getDefaultTab(canViewBuildLogs),
    tabIds: TAB_IDS,
  });

  const customTabTracking = (newTabId?: string) => {
    if (newTabId === BuildDetailsTabId.buildCache) {
      trackBuildCacheOfBuildTab();
    }
  };

  const onTabChange = (newTabId: string) => {
    trackTabChange(newTabId);
    customTabTracking(newTabId);
    selectTab(newTabId);
  };

  useEffect(() => {
    trackTabChange(tabId || 'unset', true);
    customTabTracking(tabId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let hideVmMonitoring = false;
  if (!build.triggeredAt) {
    hideVmMonitoring = true;
  } else {
    const triggeredAt = DateTime.fromISO(build.triggeredAt);
    const thirtyDaysAgo = DateTime.now().minus({ days: 30 });
    hideVmMonitoring = triggeredAt < thirtyDaysAgo;
  }

  return (
    <BitkitTabs.Root lazyMount onValueChange={({ value }) => onTabChange(value)} unmountOnExit value={tabId}>
      <BitkitTabs.List marginBlockEnd="32">
        {canViewBuildLogs && <BitkitTabs.Trigger value={BuildDetailsTabId.log}>Build log</BitkitTabs.Trigger>}
        <BitkitTabs.Trigger badge={getTestsTabBadge(testSummary.failed)} value={BuildDetailsTabId.tests}>
          Tests
        </BitkitTabs.Trigger>
        {canViewBuildArtifacts && (
          <BitkitTabs.Trigger
            badge={getArtifactsBadge({ count: artifacts.length + htmlReports.length })}
            value={BuildDetailsTabId.artifacts}
          >
            Artifacts
          </BitkitTabs.Trigger>
        )}
        {canViewBuildCache && user && (
          <BitkitTabs.Trigger
            icon={canSpeedUpBuild ? IconWarningYellow : undefined}
            value={BuildDetailsTabId.buildCache}
          >
            Build Cache
          </BitkitTabs.Trigger>
        )}
        {!selfHostedAgent && (
          <BitkitTabs.Trigger disabled={hideVmMonitoring} value={BuildDetailsTabId.resources}>
            VM monitoring
          </BitkitTabs.Trigger>
        )}
        <BitkitTabs.Trigger value={BuildDetailsTabId.details}>Details</BitkitTabs.Trigger>
      </BitkitTabs.List>
      <BitkitTabs.ContentGroup>
        {canViewBuildLogs && (
          <BitkitTabs.Content value={BuildDetailsTabId.log}>
            <AIAssistantCard marginBlockEnd="32" />
            {/* NOTE: We should only show loading state for structured logs if we know for sure if it is an archived completed build. */}
            <BuildLogContainer
              isLoading={isSummaryLoading && isBuildFinished(initBuild)}
              showStructuredBuildLog={showStructuredBuildLog}
            />
          </BitkitTabs.Content>
        )}
        <BitkitTabs.Content value={BuildDetailsTabId.tests}>
          <BuildTestResults
            appSlug={app.slug}
            artifactsUrl={canViewBuildArtifacts ? buildDetailsPagePath(app.slug, build.slug, 'artifacts') : undefined}
            isLoading={isBuildNotFinished(build) || !isTestResultsFetched}
            testResults={buildTestResults}
            testResultsError={
              buildTestResults?.testResultsError ||
              (testResultsRequestError ? String(getStandardErrorMessage(testResultsRequestError)) : undefined)
            }
            updateDeployStep={showDeployToBitriseIoWarning(summary)}
            updateDeployStepUrl={canChangeSettings ? workflowEditorPath(app.slug, build.triggeredWorkflow) : undefined}
            upgradePlan={Boolean(account && !account.testReportingAvailable)}
            upgradePlanUrl={planSelectorPagePath(account?.slug || '')}
          />
        </BitkitTabs.Content>
        {canViewBuildArtifacts && (
          <BitkitTabs.Content value={BuildDetailsTabId.artifacts}>
            <BuildArtifactList
              appSlug={app.slug}
              buildSlug={build.slug}
              isRunning={isBuildNotFinished(build)}
              triggeredWorkflow={build.triggeredWorkflow || ''}
            />
          </BitkitTabs.Content>
        )}
        {canViewBuildCache && user && (
          <BitkitTabs.Content value={BuildDetailsTabId.buildCache}>
            {tabId === BuildDetailsTabId.buildCache && <BuildCacheOfBuild />}
          </BitkitTabs.Content>
        )}
        {!selfHostedAgent && (
          <BitkitTabs.Content value={BuildDetailsTabId.resources}>
            <ResourceUsageMonitoring isEnabled={tabId === 'resources'} />
          </BitkitTabs.Content>
        )}
        <BitkitTabs.Content value={BuildDetailsTabId.details}>
          <Details showStructuredBuildLog={showStructuredBuildLog} />
        </BitkitTabs.Content>
      </BitkitTabs.ContentGroup>
    </BitkitTabs.Root>
  );
};

export default BuildDetailsTabs;
